<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        Form, ToggleProductMatches, Heading, Link, Icon, QUERY_STATE, SESSION_ORGANIZATION,
    } from '@smatch/cassiopeia-web-components';

    // COMPONENTS
    import { BadgeNew, BadgeMatch, BadgeReserved, BadgePrivate } from 'src/app/routes/auctions/components/index.js';

    // PLUGINS
    import { t } from 'svelte-i18n';
    import { getContext } from 'svelte';

    // TYPES
    import type { Auction, Organization } from 'src/app/types';

    /* EXPORTS --------------------------------------------------------------------------------- */

    export let auction: Auction;
    export let isPreview: boolean = false;

    /* INITIALIZE ------------------------------------------------------------------------------ */

    const SCOPE: string = getContext('scope');

    /* INTERNALS ------------------------------------------------------------------------------- */

    let showOnlyMatches: boolean;

    const hasAuctionFinancing = (auction: Auction): boolean => {
        if (auction.finance === undefined) {
            return false;
        }
        return auction.finance.some(({ status }) => [ 'approved', 'open' ].includes(status));
    };

    const hasBuyerFinancing = (organization: Organization): boolean => {
        if (organization.payment?.finance === undefined) {
            return false;
        }
        // @ts-expect-error
        return organization.payment.finance.some(({ status }) => [ 'approved', 'open' ].includes(status));
        // TODO: Fix Pegasus Organization Type (Optional Financing Block)
    };

    // @ts-expect-error
    $: showFinancing = hasAuctionFinancing(auction) && hasBuyerFinancing($SESSION_ORGANIZATION);
    // TODO: Fix Pegasus Organization Type

    $: QUERY_STATE.setParameter('showOnlyMatches', showOnlyMatches, SCOPE);

</script>

<header class:s8-preview = { isPreview }>

    <div class = 's8-title'>
        {#if !isPreview }
            <Link url = '../'>
                <Icon key = 'arrowLeft' css = '!h-5 inline-block'/>
            </Link>
        {/if}

        <Heading size = 'md' css = '!mb-0'>
            { auction.name }
            {#if auction.duration?.start !== undefined }
                <BadgeNew date = { auction.duration.start }/>
            {/if}
            {#if auction.isReserved }
                <BadgeReserved/>
            {/if}
            {#if auction.accessCode === 'private'}
                <BadgePrivate />
            {/if}
        </Heading>
    </div>

    <ul class = 's8-teasers'>
        <li>
            <Icon key = 'badgeCheck' css = 'stroke-success h-3 mt-0.5'/>
            { $t('component.auction.item.teaser.seller_verification.heading') }
        </li>
        {#if auction.authenticityCode !== 'none'}
            <li>
                <Icon key = 'tag' css = 'stroke-quaternary h-3 mt-0.5'/>
                { $t('component.auction.item.teaser.authenticity.heading') }
            </li>
        {/if}
        {#if showFinancing && !isPreview}
            <li>
                <Icon key = 'squareCheck' css = 'stroke-tertiary h-3 mt-0.5'/>
                { $t('component.auction.item.teaser.financing.heading') }
            </li>
        {/if}
    </ul>

    {#if !isPreview }
        {#if auction.match.rate > 0 && auction.match.rate !== 1}
            <div class = 's8-filter'>
                <BadgeMatch rate = { auction.match.rate } size = 'sm' items = { auction.match.items }/>

                <Form id = 'filter-products'>
                    <ToggleProductMatches bind:isChecked = { showOnlyMatches } />
                </Form>
            </div>
        {/if}
    {/if}

</header>

<style lang='postcss'>

    header {
        @apply fixed px-3 top-0 left-0 right-0 h-16 md:h-12 z-20 bg-light shadow-md;
        @apply flex flex-col justify-center sm:items-start md:flex-row md:justify-between md:items-center;

        &.s8-preview {
            @apply mt-36 xxs:mt-32 xs:mt-28 sm:mt-24 md:mt-20 lg:mt-16;
        }

        div.s8-title {
            @apply flex gap-x-2 items-center;
        }

        div.s8-filter {
            @apply flex gap-x-2 items-center;

            :global(label) {
                @apply whitespace-nowrap;
            }
        }

        ul.s8-teasers {
            @apply gap-x-4 hidden lg:flex;

            li {
                @apply flex flex-row gap-x-1 whitespace-nowrap;
            }
        }
    }

</style>